import { Navigate } from 'react-router-dom'

import async from '@/components/Async'
// Guards
import AuthGuard from '@/components/guards/AuthGuard'
import GuestGuard from '@/components/guards/GuestGuard'
import useAuth from '@/hooks/useAuth'
// All pages that rely on 3rd party components (other than MUI) are
// loaded asynchronously, to keep the initial JS bundle to a minimum size
// Layouts
import AuthLayout from '@/layouts/Auth'
import DashboardLayout from '@/layouts/Dashboard'
import ErrorLayout from '@/layouts/Error'
// Auth components
import AuthActionHandler from '@/pages/auth/AuthActionHandler'
import Page404 from '@/pages/auth/Page404'
import Page500 from '@/pages/auth/Page500'
import SignIn from '@/pages/auth/SignIn'
import SignUp from '@/pages/auth/SignUp'

import PermissionGuard from './components/guards/PermissionGuard'
// Dashboard components
import { NotAuthorized } from './pages/pages/NotAuthorized'

const StorePicosAudits = async(() => import('./pages/store/StorePicosAudits'))
const StorePicOSAudit = async(() => import('./pages/store/StorePicOSAudit'))
const StoreJBPAudits = async(
  () => import('./pages/reporting/jbp/StoreJBPAudits'),
)
const StoreJBPAudit = async(() => import('./pages/reporting/jbp/StoreJBPAudit'))
const StoreAssignments = async(() => import('./pages/admin/StoreAssignments'))

// Reporting components
const DXOReports = async(() => import('./pages/reporting/DXOReports'))

const AuthRedirect = () => {
  const { isAuthenticated } = useAuth()
  return isAuthenticated ? (
    <Navigate to="/reporting/user" replace />
  ) : (
    <Navigate to="/auth/sign-in" replace />
  )
}

const authRoutes = [
  {
    path: 'auth',
    element: <AuthLayout />,
    children: [
      {
        path: 'sign-in',
        element: (
          <GuestGuard>
            <SignIn />
          </GuestGuard>
        ),
      },
      {
        path: 'sign-up',
        element: (
          <GuestGuard>
            <SignUp />
          </GuestGuard>
        ),
      },
      {
        path: 'action',
        element: <AuthActionHandler />,
      },
    ],
  },
]

const routes = [
  {
    path: '/',
    element: <AuthRedirect />,
  },
  {
    path: '/not-authorized',
    element: <NotAuthorized />,
  },
  ...authRoutes,
  {
    path: 'store/:storeId',
    element: (
      <AuthGuard>
        <PermissionGuard requiredPermissions={[]}>
          <DashboardLayout />
        </PermissionGuard>
      </AuthGuard>
    ),
    children: [
      {
        path: 'audits',
        element: <StorePicosAudits />,
      },
      {
        path: 'audits/:date',
        element: <StorePicOSAudit />,
      },
      {
        path: 'jbp-audits',
        element: <StoreJBPAudits />,
      },
      {
        path: 'jbp-audits/:jbpAuditId',
        element: <StoreJBPAudit />,
      },
    ],
  },
  {
    path: '',
    element: (
      <AuthGuard>
        <PermissionGuard requiredPermissions={[]}>
          <DashboardLayout />
        </PermissionGuard>
      </AuthGuard>
    ),
    children: [
      {
        path: 'admin/store-assignments',
        element: (
          <PermissionGuard requiredPermissions={['CAN_ASSIGN_USER_STORES']}>
            <StoreAssignments />
          </PermissionGuard>
        ),
      },
    ],
  },
  {
    path: 'reporting',
    element: (
      <AuthGuard>
        <PermissionGuard requiredPermissions={[]}>
          <DashboardLayout />
        </PermissionGuard>
      </AuthGuard>
    ),
    children: [
      {
        path: '*',
        element: <DXOReports />,
      },
    ],
  },
  {
    path: 'error',
    element: <ErrorLayout />,
    children: [
      {
        path: '404',
        element: <Page404 />,
      },
      {
        path: '500',
        element: <Page500 />,
      },
      {
        path: 'not-authorized',
        element: <NotAuthorized />,
      },
    ],
  },
  {
    path: '*',
    element: <ErrorLayout />,
    children: [
      {
        path: '*',
        element: <Page404 />,
      },
    ],
  },
]

export default routes
